<template>
    <v-card>
        <v-toolbar
            v-show="!is_full_page"
            dark
            color="primary"
        >
            <v-toolbar-title>Search Saved Messages</v-toolbar-title>
        </v-toolbar>

        <v-card-text>
            <v-card-title>

                <v-row>
                    <v-col class="flex-grow-1">
                        <v-text-field
                            label="Search"
                            v-model="full_text"
                            clearable
                            v-on:input="doSearch"
                            autofocus
                            autocomplete="off"
                            v-on:keypress="onKeyPress"
                            ref="search_box"
                        >
                            <v-icon
                                v-show="!is_full_page"
                                slot="prepend"
                                v-on:click="closeDialog"
                            >
                                mdi-arrow-left
                            </v-icon>

                            <template v-slot:append-outer>
                                <v-tooltip
                                    bottom
                                >
                                    <template v-slot:activator="{ on }">
                                        <v-icon
                                            v-on="on"
                                            v-on:click="onCreateSavedMessage"
                                        >
                                            mdi-plus-circle
                                        </v-icon>
                                    </template>
                                    Create Saved Message
                                </v-tooltip>
                            </template>
                        </v-text-field>
                    </v-col>
                    <v-col class="flex-grow-0">
                        <v-btn
                            style="margin-top: 15px"
                            v-on:click="onClickIntroductions"
                        >
                            Introductions...
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card-title>

            <v-list three-line>
                <v-list-item
                    v-for="(saved_message, index) in saved_messages"
                    :key="index"
                    v-on:click="onClickRow(saved_message)"
                    :ref="getSavedMessageRef(index)"
                >
                    <v-list-item-content>
                        <v-list-item-title v-html="getTitle(saved_message.title)"></v-list-item-title>
                        <v-list-item-subtitle>
                            <ul>
                                <li
                                    v-for="(pattern, pidx) in saved_message.message_patterns"
                                    :key="'pkey_'+pidx"
                                >
                                    {{pattern}}
                                </li>
                            </ul>
                        </v-list-item-subtitle>
                    </v-list-item-content>

                    <v-list-item-avatar
                        v-if="hasVcard(saved_message)"
                        tile
                        right
                        tag="b"
                    > 
                        <v-icon large>mdi-card-account-details-outline</v-icon>
                    </v-list-item-avatar>

                    <v-list-item-avatar
                        v-if="hasBlob(saved_message)"
                        tile
                        right
                    >
                        <v-img
                            :src="getFirstBlobSrcURL(saved_message)"
                            contain
                        >
                        </v-img>
                    </v-list-item-avatar>

                    <v-list-item-action>
                        <v-menu
                            bottom
                            offset-y
                        >
                            <template v-slot:activator="{ on }">
                                <v-icon
                                    v-on="on"
                                >
                                    mdi-dots-vertical
                                </v-icon>
                            </template>

                            <v-list>
                                <v-list-item
                                    v-on:click="onEditSavedMessage(saved_message)"
                                >
                                    <v-list-item-title>Edit...</v-list-item-title>
                                </v-list-item>
                                <v-list-item
                                    v-on:click="onDeleteSavedMessage(saved_message)"
                                >
                                    <v-list-item-title>Delete...</v-list-item-title>
                                </v-list-item>

                                <v-divider
                                    v-if="include_send_and_edit || include_send"
                                />

                                <v-list-item
                                    v-if="include_send_and_edit"
                                    v-on:click="onSendAndEdit(saved_message)"
                                >
                                    <v-list-item-title>Edit before sending...</v-list-item-title>
                                </v-list-item>
                                <v-list-item
                                    v-if="include_send"
                                    v-on:click="onSendMessage(saved_message)"
                                >
                                    <v-list-item-title>Send...</v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </v-list-item-action>
                </v-list-item>
            </v-list>
        </v-card-text>

        <m-saved-message-dialog
            ref="saved-message-dialog"
            v-on:ok="onUpsertSavedMessage"
            v-on:close-dialog="onCloseSavedMessageDialog"
        />
    </v-card>
</template>

<script>
const { v4: uuidv4 } = require('uuid');
const BlobMetaData = require('@/js/BlobMetaData.js');

export default {

    props: {

        is_full_page: {
            type: Boolean,
            default: () => {
                return false;
            }
        },

        include_send: {
            type: Boolean,
            default: () => {
                return true;
            }
        },

        include_send_and_edit: {
            type: Boolean,
            default: () => {
                return true;
            }
        },

        organization_id: {
            type: String,
            default: function() { return null; },
        },
    },

    data() {
        return {
            full_text: '',
            saved_messages: [],
            selected_item: -1,
            showing_edit_saved_message_dialog: false,
            blob_meta_data: [],
        }
    },

    created() {
        if ( !this.is_full_page )
        {
            document.addEventListener("keydown", this.nextItem);
        }
    },

    destroyed() {
        if ( !this.is_full_page )
        {
            document.removeEventListener("keydown", this.nextItem);
        }
    },

    mounted() {
        this.doSearch();
    },

    methods: {

        onCloseSavedMessageDialog()
        {
            this.showing_edit_saved_message_dialog = false;
        },

        getSavedMessageRef(index)
        {
            return "saved_message_box_" + index;
        },

        nextItem(key_pressed)
        {
            // if showing the edit saved message dialog, don't run this logic
            // (creates broken behavior)
            if ( this.showing_edit_saved_message_dialog ) return;

            if ( key_pressed.keyCode == 38
                && this.selected_item >= 0 )
            {
                this.selected_item--;
            }
            if ( key_pressed.keyCode == 40
                && this.selected_item < ( this.saved_messages.length-1 ) )
            {
                this.selected_item++;
            }

            if (this.selected_item == -1)
            {
                this.$refs["search_box"].focus();
                return;
            }

            let selected_key = 'saved_message_box_' + this.selected_item;
            let selected_item = this.$refs[selected_key][0];

            selected_item.$el.focus();
        },

        getTitle(raw_title)
        {
            if ( this.full_text && this.full_text != null )
            {
                return '#<b>' + this.getHighlightedText(raw_title) + '</b>';
            }

            return '#<b>' + raw_title + '</b>';
        },

        closeDialog()
        {
            this.selected_item = -1;
            this.$emit('close-dialog');
        },

        getHighlightedText(raw_text)
        {
            let normalized_full_text = this.full_text.toLowerCase();
            let normalized_text = raw_text.toLowerCase();

            let start_idx = normalized_text.indexOf(normalized_full_text);

            if ( start_idx < 0 ) return raw_text;

            let before_match = raw_text.substring(0, start_idx);
            let highlighted = raw_text.substring(start_idx, start_idx + normalized_full_text.length);
            let after_match = raw_text.substring(start_idx + normalized_full_text.length, normalized_text.length);

            return before_match + '<mark>' + highlighted + '</mark>' + after_match;
        },

        onKeyPress(event)
        {
            if ( event.key == '#' && this.full_text == '')
            {
                this.closeDialog();
            }
        },

        doSearch() {
            let search = {
                'organization_id': this.organization_id,
                'full_text': this.full_text,
            };

            this.$rc.showSpinner();

            this.$rc.searchSavedMessages(
                search,
                (data) => {
                    this.$rc.hideSpinner();
                    this.saved_messages = data.results;

                    let blob_ids = [];
                    this.saved_messages
                        .filter((msg) => this.hasBlob(msg))
                        .map((msg) => blob_ids.push.apply(blob_ids, msg.blob_ids));
                    this.getBlobDataFromRedChirp(blob_ids);
                },
                () => {
                    this.$rc.alert(
                        'Error Retrieving Saved Messages',
                        'There was an error retrieving Saved Messages. Please reload the page to try again.'
                    );
                },
            );
        },

        onUpsertSavedMessage(saved_message)
        {
            this.$rc.showSpinner();

            this.$rc.putSavedMessage(
                saved_message,
                (data) => {
                    this.$rc.hideSpinner();

                    this.$rc.snackbar('Saved message created', 3000);
                    this.doSearch();
                },
                () => {
                    this.$rc.alert(
                        "Failed to create Saved Message",
                        "There was an error creating the Saved Message, please try again."
                    );
                },
            );
        },

        onSendMessage(saved_message) {
            this.$emit('send', saved_message);
            this.closeDialog();
        },

        onSendAndEdit(saved_message) {
            this.$emit('send-and-edit', saved_message);
            this.closeDialog();
        },

        onCreateSavedMessage() {
            let saved_message = {
                'organization_id' : this.$rc.$state.profile.employee_account.organization_id,
                'saved_message_id' : uuidv4(),
                'authored_by_employee_id' : this.$rc.$state.profile.employee_account.employee_id,
                'limit_to_employee_id' : this.$rc.$state.profile.employee_account.employee_id,
                'available_to_all_team_members' : false,
                'creation_time' : Date.now(),
                'message_patterns' : ['Hi {customer-first-name}, this is {employee-full-name} from {organization-label}, how can I help you today?', 'Hi!  How can I help you today?'],
                'use_legacy_variables': false,
                'title' : null,
            };

            this.showing_edit_saved_message_dialog = true;
            this.$refs['saved-message-dialog'].showDialog(saved_message);
        },

        onEditSavedMessage(saved_message) {
            this.showing_edit_saved_message_dialog = true;
            this.$refs['saved-message-dialog'].showDialog(saved_message);
        },

        onDeleteSavedMessage(saved_message) {
            this.$rc.confirm(
                'Delete Saved Message',
                'Are you sure you want to delete this saved message?',
                [
                    {'text' : 'Cancel', 'value' : 'cancel' },
                    {'text' : 'Delete', 'value' : 'confirmed' },
                ],
                (response) => {
                    if ( !saved_message.limit_to_employee_id )
                    {
                        this.onGiveNotOriginalAuthorWarning(saved_message);
                        return;
                    }

                    if ( response != 'confirmed' ) return;

                    this.$rc.deleteSavedMessage(
                        saved_message.organization_id,
                        saved_message.saved_message_id,
                        (data) => {
                            this.$rc.snackbar('Saved message deleted', 3000);
                            this.doSearch();
                        },
                        () => {
                            this.$rc.alert(
                                'Error Deleting Saved Message',
                                'The saved message could not be deleted. Please try again.'
                            );
                        },
                    );
                },
            );
        },

        onGiveNotOriginalAuthorWarning(saved_message)
        {
            this.$rc.confirm(
                'Global Saved Message Warning',
                'Other people have access to this Saved Message. Are you sure you want to delete this message for everyone?',
                [
                    {'text' : 'Cancel', 'value' : 'cancel' },
                    {'text' : 'Delete', 'value' : 'confirmed' },
                ],
                (response) => {
                    if ( response != 'confirmed' ) return;

                    this.$rc.deleteSavedMessage(
                        saved_message.organization_id,
                        saved_message.saved_message_id,
                        (data) => {
                            this.$rc.snackbar('Saved message deleted', 3000);
                            this.doSearch();
                        },
                        () => {
                            this.$rc.alert(
                                'Error Deleting Saved Message',
                                'The saved message could not be deleted. Please try again.'
                            );
                        },
                    );
                },
            );
        },

        onClickRow(item) {
            if ( this.is_full_page )
            {
                this.onEditSavedMessage(item);
            }
            else
            {
                this.onSendAndEdit(item);
            }
        },

        onClickIntroductions()
        {
            this.$emit('click-introductions');
        },

        getBlobDataFromRedChirp(blob_ids)
        {
            this.$rc.showSpinner();
            this.$rc.getBlobMetaDataMultiple(
                blob_ids,

                (data) => {
                    this.$rc.hideSpinner();

                    this.blob_meta_data = [];
                    data.meta_data.forEach((meta_data) =>
                    {
                        this.blob_meta_data.push(new BlobMetaData(meta_data));
                    });
                },

                () => {
                    this.$rc.hideSpinner();
                    this.blob_meta_data = [];
                    this.$rc.alert('Error', 'Unable to get blob meta data');
                }
            );
        },

        getFirstBlobSrcURL(saved_message)
        {
            if (! this.hasBlob(saved_message)) return "";

            let blob_id = saved_message.blob_ids[0];
            let meta_data = this.blob_meta_data.find((meta) => meta.getBlobId() == blob_id);
            if (! meta_data) return "";

            return this.$rc.getBlobEndpoint(meta_data);
        },

        hasBlob(saved_message)
        {
            return saved_message && saved_message.blob_ids && saved_message.blob_ids.length > 0;
        },

        hasVcard(saved_message)
        {
            return saved_message && saved_message.vcard_id;
        }
    },

    watch: {
        full_text(newValue, oldValue) {
            this.doSearch();
        },
    },

    computed: {
        pageframe_color() {
            if ( this.$route.params.organization_id !== this.$rc.$state.organization_id )
                return '#5D647C';
            else
                return '#F24653';
        },
    },
}</script>

<style lang="scss" scoped>

</style>